import { useForm } from "@tanstack/react-form";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";
import { z } from "zod";

import { disableMfa, profileMfaQueryOptions, startMfaSetup, verifyMfaSetup } from "../../api/mfa";
import { useAskAgain } from "../../api/refresh";
import { changePassword, useSession } from "../../api/session";
import { AuthenticatorSetup } from "../../components/authenticator-setup";
import { PageHeader } from "../../components/shell/app-shell";
import { Button } from "../../components/ui/button";
import { Card, CardHeader } from "../../components/ui/card";
import { ConfirmDialog } from "../../components/ui/confirm-dialog";
import { Field, Input } from "../../components/ui/field";
import { Pill } from "../../components/ui/pill";
import { MEMBER_ROLE_LABELS } from "../../domain/settings";
import { fieldErrorMessage } from "../../lib/form";
import { showToast } from "../../lib/toast";

export const Route = createFileRoute("/settings/profile")({
	// Prefetch through the Query cache so the two-step verification card can use useSuspenseQuery without a loading flash.
	loader: ({ context }) => context.queryClient.query({ ...profileMfaQueryOptions(), staleTime: "static" }),
	head: () => ({ meta: [{ title: "Profile · Ravnar" }] }),
	staticData: { crumbs: ["Settings", "Profile"] },
	component: ProfilePage,
});

const changePasswordFormSchema = z
	.object({
		currentPassword: z.string().min(1, "Current password is required"),
		newPassword: z.string().min(12, "Use at least 12 characters").max(128, "Use at most 128 characters"),
		confirmPassword: z.string(),
	})
	.refine(value => value.confirmPassword === value.newPassword, {
		message: "The passwords do not match",
		path: ["confirmPassword"],
	})
	.refine(value => value.newPassword !== value.currentPassword, {
		message: "Choose a password you are not using now",
		path: ["newPassword"],
	});

function ProfilePage() {
	const { member } = useSession();
	const askAgain = useAskAgain();
	const mutation = useMutation({
		mutationFn: changePassword,
		// Both outcomes add an audit log entry, which arrives through a queue a moment after this answer.
		onSettled: () => askAgain(["settings", "audit-log"]),
	});

	const form = useForm({
		defaultValues: { currentPassword: "", newPassword: "", confirmPassword: "" },
		// Zod schemas are Standard Schema validators, so no adapter is needed.
		validators: { onChange: changePasswordFormSchema, onSubmit: changePasswordFormSchema },
		onSubmit: async ({ value, formApi }) => {
			try {
				await mutation.mutateAsync({ currentPassword: value.currentPassword, newPassword: value.newPassword });
			} catch {
				// The alert below shows mutation.error. Rethrowing would make handleSubmit reject unhandled.
				return;
			}
			formApi.reset();
			showToast("Password changed.");
		},
	});

	return (
		<>
			<PageHeader title="Profile" description="Your account and how you sign in." />
			<div className="flex flex-col gap-3.5">
				<Card>
					<CardHeader title="Account" />
					<dl className="grid grid-cols-[120px_1fr] gap-x-4 gap-y-2 px-4 py-3 text-body">
						<dt className="text-ink-3">Name</dt>
						<dd className="text-ink">{member.name}</dd>
						<dt className="text-ink-3">Email</dt>
						<dd className="text-ink">{member.email}</dd>
						<dt className="text-ink-3">Role</dt>
						<dd className="text-ink">{MEMBER_ROLE_LABELS[member.role]}</dd>
					</dl>
				</Card>
				<TwoStepVerificationCard />
				<Card>
					<CardHeader title="Change password" />
					<form
						noValidate
						className="flex max-w-[380px] flex-col gap-4 p-4"
						onSubmit={event => {
							event.preventDefault();
							event.stopPropagation();
							void form.handleSubmit();
						}}
					>
						<form.Field name="currentPassword">
							{field => (
								<Field label="Current password" error={fieldErrorMessage(field.state.meta)}>
									<Input
										type="password"
										name={field.name}
										autoComplete="current-password"
										value={field.state.value}
										onBlur={field.handleBlur}
										onChange={event => {
											field.handleChange(event.target.value);
										}}
									/>
								</Field>
							)}
						</form.Field>
						<form.Field name="newPassword">
							{field => (
								<Field label="New password" error={fieldErrorMessage(field.state.meta)}>
									<Input
										type="password"
										name={field.name}
										autoComplete="new-password"
										value={field.state.value}
										onBlur={field.handleBlur}
										onChange={event => {
											field.handleChange(event.target.value);
										}}
									/>
								</Field>
							)}
						</form.Field>
						<form.Field name="confirmPassword">
							{field => (
								<Field label="Confirm new password" error={fieldErrorMessage(field.state.meta)}>
									<Input
										type="password"
										name={field.name}
										autoComplete="new-password"
										value={field.state.value}
										onBlur={field.handleBlur}
										onChange={event => {
											field.handleChange(event.target.value);
										}}
									/>
								</Field>
							)}
						</form.Field>
						<p className="text-caption text-ink-3">Use at least 12 characters.</p>
						{mutation.isError ? (
							<p role="alert" className="text-caption text-alert">
								{mutation.error.message}
							</p>
						) : null}
						<form.Subscribe selector={state => [state.canSubmit, state.isSubmitting]}>
							{([canSubmit, isSubmitting]) => (
								<div>
									<Button type="submit" variant="primary" disabled={!canSubmit || isSubmitting}>
										{isSubmitting ? "Changing…" : "Change password"}
									</Button>
								</div>
							)}
						</form.Subscribe>
					</form>
				</Card>
			</div>
		</>
	);
}

/**
 * A member turns an authenticator app on for themselves here: Set up shows a QR code and the secret to scan or type,
 * confirming it with one code turns it on. Once on, only a confirmed dialog turns it off — there is no recovery code,
 * so losing the app means asking an owner or admin to reset it (plan 069).
 */
function TwoStepVerificationCard() {
	const { member } = useSession();
	const queryClient = useQueryClient();
	const askAgain = useAskAgain();
	const { data: mfa } = useSuspenseQuery(profileMfaQueryOptions());
	// Set once startMfaSetup hands out a secret to scan; cleared once it is confirmed or cancelled.
	const [secret, setSecret] = useState<string | undefined>();
	const [isConfirmingTurnOff, setIsConfirmingTurnOff] = useState(false);

	// Both outcomes add an audit log entry, which arrives through a queue a moment after this answer.
	const refreshMfa = () =>
		Promise.all([queryClient.invalidateQueries({ queryKey: ["settings"] }), askAgain(["settings", "audit-log"])]);

	const setupMutation = useMutation({
		mutationFn: startMfaSetup,
		onSuccess: response => {
			setSecret(response.secret);
		},
	});
	const verifyMutation = useMutation({ mutationFn: verifyMfaSetup, onSuccess: refreshMfa });
	const disableMutation = useMutation({ mutationFn: disableMfa, onSuccess: refreshMfa });

	const startSetup = async () => {
		setupMutation.reset();
		try {
			await setupMutation.mutateAsync();
		} catch {
			// The paragraph below shows setupMutation.error.
		}
	};

	const cancelSetup = () => {
		setSecret(undefined);
		verifyMutation.reset();
	};

	const verifySetup = async (code: string) => {
		try {
			await verifyMutation.mutateAsync({ code });
		} catch {
			// AuthenticatorSetup's own error prop shows verifyMutation.error.
			return;
		}
		setSecret(undefined);
		showToast("Two-step verification is on.");
	};

	const turnOff = async () => {
		try {
			await disableMutation.mutateAsync();
		} catch {
			// The dialog's own error prop shows disableMutation.error, which is why it does not close itself.
			return;
		}
		setIsConfirmingTurnOff(false);
		showToast("Two-step verification is off.");
	};

	let body: ReactNode;
	if (secret !== undefined) {
		body = (
			<>
				<AuthenticatorSetup
					email={member.email}
					secret={secret}
					error={verifyMutation.error?.message}
					isPending={verifyMutation.isPending}
					submitLabel="Turn on two-step verification"
					onVerify={verifySetup}
				/>
				<div>
					<Button onClick={cancelSetup}>Cancel</Button>
				</div>
			</>
		);
	} else if (mfa.enabled) {
		body = (
			<>
				<p className="flex items-center gap-2 text-body text-ink-2">
					<Pill tone="ok">On</Pill>
					You are asked for a code from your authenticator app every time you sign in.
				</p>
				{mfa.requiredByOrganization ? (
					<p className="text-caption text-ink-3">
						Your organization requires two-step verification, so it cannot be turned off.
					</p>
				) : (
					<div>
						<Button
							className="border-alert-mid bg-alert-soft text-alert hover:bg-alert-mid hover:text-alert"
							onClick={() => {
								setIsConfirmingTurnOff(true);
							}}
						>
							Turn off two-step verification
						</Button>
					</div>
				)}
			</>
		);
	} else {
		body = (
			<>
				<p className="text-body text-ink-2">
					Sign in with a code from an authenticator app as well as your password.
				</p>
				{setupMutation.isError ? (
					<p role="alert" className="text-caption text-alert">
						{setupMutation.error.message}
					</p>
				) : null}
				<div>
					<Button
						variant="primary"
						disabled={setupMutation.isPending}
						onClick={() => {
							void startSetup();
						}}
					>
						{setupMutation.isPending ? "Starting…" : "Set up two-step verification"}
					</Button>
				</div>
			</>
		);
	}

	return (
		<Card>
			<CardHeader title="Two-step verification" />
			<div className="flex flex-col gap-4 p-4">{body}</div>
			<ConfirmDialog
				open={isConfirmingTurnOff}
				title="Turn off two-step verification?"
				confirmLabel="Turn off two-step verification"
				busy={disableMutation.isPending}
				error={disableMutation.error?.message}
				onConfirm={() => {
					void turnOff();
				}}
				onClose={() => {
					disableMutation.reset();
					setIsConfirmingTurnOff(false);
				}}
			>
				You will only need your password to sign in again.
			</ConfirmDialog>
		</Card>
	);
}
